import * as React from 'react'
import { Form, Spin, Button, Tabs, Input } from 'antd'
import { isNil, set, get, uniq, toLower, cloneDeep, camelCase, trimStart, isEmpty } from 'lodash'
import produce from 'immer'
import pluralize from 'pluralize'
import moment from 'moment'
import { showError, ValidationError } from 'helpers/errors'
import { allowPricing, createFilterUserSuppliers } from 'helpers/auth'
import Checkbox from 'elements/Checkbox'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import { Row, Col } from 'elements/Grid'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createSearchSuppliers,
  createDropdownRender,
  filterByValue,
  createSearchOperatorTag,
  createSearchJobTag,
  createSearchAssetTag,
  isReadOnly,
} from 'helpers/formViews'
import { HARD_SPACE } from 'helpers/utils'
import { stopEvent } from 'helpers/events'
import { createOrderItemsTransformSaveItemParams } from 'helpers/procurement'
import { t } from 'helpers/i18n'
import AutoComplete, { Suffix } from 'elements/AutoComplete'

const tagTypes = ['2', '1', '3', 'A', 'B', 'C', 'D', 'E']

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchSuppliers = createSearchSuppliers(this)
    this.searchOperators = createSearchOperatorTag(this)
    this.searchJobs = createSearchJobTag(this)
    this.searchAssets = createSearchAssetTag(this)
    this.transformSaveItemParams = createOrderItemsTransformSaveItemParams(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.orderItemId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        this.props.getItem(itemId).catch((error) => {
          this.props.onCancel?.()
          throw error
        }),
        this.props.getSettings({ type: this.props.settingsType ?? 'issue' }),
        this.props.getLocations(),
      ])

      const item = cloneDeep(responses[0].value.data)

      const [jobs, operators, assets, supplierDetailItems] = await Promise.all([
        this.props.user.coreUserSettings.tagSettings.tag1Enabled
          ? this.props.getJobs({ jobIds: item.jobId ? [item.jobId] : [] })
          : Promise.resolve(null),
        this.props.user.coreUserSettings.tagSettings.tag2Enabled
          ? this.props.getOperators({ operatorIds: item.operatorId ? [item.operatorId] : [] })
          : Promise.resolve(null),
        this.props.user.coreUserSettings.tagSettings.tag3Enabled
          ? this.props.getAssets({ assetIds: item.assetId ? [item.assetId] : [] })
          : Promise.resolve(null),
        item.barcode && !item.nonStock
          ? this.props
              .findByBarcode(item)
              .then((r) => this.props.getSupplierDetailItems(r.value.data.id))
              .catch(() => {}) ?? null
          : Promise.resolve(null),
      ])

      const assetName = trimStart(item.assetName, '*')
      item.assetDisplayName = item.assetId
        ? await this.props
            .getAsset(item.assetId)
            .then((r) => r.value.data.displayName)
            .catch(() => assetName)
        : assetName

      const operatorName = trimStart(item.operatorName, '*')
      item.operatorDisplayName = item.operatorId
        ? await this.props
            .getOperator(item.operatorId)
            .then((r) => r.value.data.displayName)
            .catch(() => operatorName)
        : operatorName

      const jobName = trimStart(item.jobName, '*')
      item.jobDisplayName = item.jobId
        ? await this.props
            .getJob(item.jobId)
            .then((r) => r.value.data.displayName)
            .catch(() => jobName)
        : jobName

      const tagListItems = await this.props.getTagListItems({ tagType: 'All' })

      this.setState(
        {
          item,
          fieldSettings: responses[1].value.data.fieldSettings,
          locations: get(responses[2], 'value.data.items', []),
          jobs: get(jobs, 'value.data', {}),
          operators: get(operators, 'value.data', {}),
          assets: get(assets, 'value.data', {}),
          tagListItems: get(tagListItems, 'value.data.items', []),
          supplierDetailItems: get(supplierDetailItems, 'value.data.items', []),
        },
        this.searchSuppliers
      )
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'supplierId' && !draft.item.nonStock) {
          try {
            const supplierDetailItem = draft.supplierDetailItems.find((one) => one.supplierId === value)

            if (supplierDetailItem) {
              const { supplierItemNumber, price } = supplierDetailItem
              Object.assign(draft.item, { supplierItemNumber, price })
              this.props.form.setFieldsValue({ supplierItemNumber, price })
            } else {
              draft.item.supplierItemNumber = ''
              this.props.form.setFieldsValue({ supplierItemNumber: '' })
            }
          } catch (error) {
            console.warn(error)
          }
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  setTagValue = (key, value) =>
    this.setState(
      produce((draft) => {
        const item = get(draft, `${pluralize(key)}.items`, []).find((one) => one.displayName === value)

        set(draft, `item.${key}DisplayName`, value)
        ;['id', 'barcode', 'name', 'number'].forEach((one) => {
          set(draft, `item.${camelCase(`${key}-${one}`)}`, get(item, one))
        })

        set(draft, key, item)

        this.props.form.setFieldsValue({ [`${key}DisplayName`]: value })
      })
    )

  beforeValidateFields = () => {
    const promises = []
    const { item, tagListItems, operators, jobs, assets } = this.state
    const { operatorDisplayName, jobDisplayName, assetDisplayName } = item

    if (
      operatorDisplayName &&
      this.props.user.coreUserSettings.tagSettings.tag2Enabled &&
      uniq((operators?.items ?? []).map((each) => each.displayName)).length === 1
    ) {
      promises.push(
        new Promise((resolve) => {
          this.setState(
            produce((draft) => {
              const operator = operators.items[0]

              ;['id', 'barcode', 'displayName', 'name', 'number'].forEach((one) => {
                set(draft.item, camelCase(`operator-${one}`), operator[one])
              })

              this.props.form.setFieldsValue({
                operatorDisplayName: draft.item.operatorDisplayName,
              })
            }),
            resolve
          )
        })
      )
    }

    if (
      jobDisplayName &&
      this.props.user.coreUserSettings.tagSettings.tag1Enabled &&
      uniq((jobs?.items ?? []).map((each) => each.displayName)).length === 1
    ) {
      promises.push(
        new Promise((resolve) => {
          this.setState(
            produce((draft) => {
              const job = jobs.items[0]

              ;['id', 'barcode', 'displayName', 'name', 'number'].forEach((one) => {
                set(draft.item, camelCase(`job-${one}`), job[one])
              })

              this.props.form.setFieldsValue({ jobDisplayName: draft.item.jobDisplayName })
            }),
            resolve
          )
        })
      )
    }

    if (
      assetDisplayName &&
      this.props.user.coreUserSettings.tagSettings.tag3Enabled &&
      uniq((assets?.items ?? []).map((each) => each.displayName)).length === 1
    ) {
      promises.push(
        new Promise((resolve) => {
          this.setState(
            produce((draft) => {
              const asset = assets.items[0]

              ;['id', 'barcode', 'displayName', 'name', 'number'].forEach((one) => {
                set(draft.item, camelCase(`asset-${one}`), asset[one])
              })

              this.props.form.setFieldsValue({ assetDisplayName: draft.item.assetDisplayName })
            }),
            resolve
          )
        })
      )
    }

    ;['A', 'B', 'C', 'D', 'E'].forEach((type) => {
      if (this.props.user.coreUserSettings.tagSettings[`tag${type}Enabled`]) {
        const filteredTagListItems = tagListItems
          .filter((each) => each.tagType === type)
          .map((each) => each.description)
          .filter((each) => toLower(each).indexOf(toLower(get(item, `tag${type}`))) > -1)

        if (filteredTagListItems.length === 1) {
          promises.push(
            new Promise((resolve) => {
              this.setState(
                produce((draft) => {
                  set(draft, `item.tag${type}`, filteredTagListItems[0])

                  this.props.form.setFieldsValue({ [`tag${type}`]: filteredTagListItems[0] })
                }),
                resolve
              )
            })
          )
        }
      }
    })

    return Promise.all(promises)
  }

  render() {
    const { customer, user } = this.props
    const { item, fieldSettings, operators, jobs, assets, tagListItems, suppliers } = this.state

    if (!fieldSettings || isNil(suppliers)) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)
    const tagsEnabled = tagTypes.some((type) => get(user, `coreUserSettings.tagSettings.tag${type}Enabled`))
    const createDate = moment(this.props.parentRecord.createDate).startOf('day')

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Tabs
            activeKey={this.state.tabsActiveKey || 'item'}
            onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
          >
            <Tabs.TabPane key="item" tab={t('item')} forceRender>
              <Row>
                <Col half>
                  <Form.Item label={createLabel('barcode')}>
                    <Input value={item.barcode} disabled />
                  </Form.Item>
                </Col>
                <Col half>
                  <Form.Item label={createLabel('supplierId')}>
                    {createFieldDecorator('supplierId')(
                      <Select
                        onChange={(value) => this.setItemValue('supplierId', value)}
                        dropdownRender={createDropdownRender(suppliers)}
                        onSearch={this.searchSuppliers}
                        allowClear={false}
                        showSearch
                      >
                        <Option value={0}>{t('notAssigned')}</Option>
                        {(suppliers?.items ?? [])
                          .filter((each) => each.id)
                          .filter(createFilterUserSuppliers(item.supplierId))
                          .map((each) => (
                            <Option key={each.id} value={each.id}>
                              <span {...getOptionProps(each)}>{each.name}</span>
                            </Option>
                          ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col half>
                  <Form.Item label={createLabel('itemNumber')}>
                    {createFieldDecorator('itemNumber')(<Input />)}
                  </Form.Item>
                </Col>
                <Col half>
                  <Form.Item label={createLabel('supplierItemNumber')}>
                    <Input value={item.supplierItemNumber} disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={createLabel('description')}>
                {createFieldDecorator('description')(<Input />)}
              </Form.Item>
              <Row>
                <Col half>
                  <Form.Item label={createLabel('packageSizeUom')}>
                    <Input value={item.packageSizeUom} disabled />
                  </Form.Item>
                  <Form.Item label={createLabel('locationId')}>
                    {createFieldDecorator('locationId')(
                      <Select allowClear={false}>
                        {(this.state.locations ?? []).map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                  <Row>
                    <Col xs={allowPricing() ? 12 : 24}>
                      <Form.Item label={createLabel('quantityIssued')}>
                        {createFieldDecorator('quantityIssued')(<InputNumber />)}
                      </Form.Item>
                    </Col>
                    {allowPricing() && (
                      <Col xs={12}>
                        <Form.Item label={createLabel('issuedPrice')}>
                          <InputNumber value={item.issuedPrice} currency disabled />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row>
                    <Col xs={allowPricing() ? 12 : 24}>
                      <Form.Item label={createLabel('quantityOrdered')}>
                        <InputNumber value={item.quantityOrdered} disabled />
                      </Form.Item>
                    </Col>
                    {allowPricing() && (
                      <Col xs={12}>
                        <Form.Item label={createLabel('price')}>
                          {createFieldDecorator('price')(<InputNumber readOnly={readOnly} currency />)}
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Form.Item label={createLabel('leadTime')}>
                    <DatePicker
                      value={createDate.clone().add(item.leadTime, 'days')}
                      onChange={(value) =>
                        this.setItemValue('leadTime', moment(value).startOf('day').diff(createDate, 'days'))
                      }
                      disabledDate={(value) => moment(value).startOf('day').diff(createDate, 'days') < 0}
                    />
                  </Form.Item>
                </Col>
                <Col half>
                  <Form.Item label={createLabel('manufacturer')}>
                    <Input value={item.manufacturer} disabled />
                  </Form.Item>
                  <Form.Item label={createLabel('binLocation')}>
                    <Input value={item.binLocation} disabled />
                  </Form.Item>
                  <Form.Item label={createLabel('source')}>
                    <Input value={item.source} disabled />
                  </Form.Item>
                  <Row>
                    <Col xs={12}>
                      <Form.Item label={HARD_SPACE}>
                        {createFieldDecorator('expedite', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('expedite')}</Checkbox>
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item label={HARD_SPACE}>
                        {createFieldDecorator('taxable', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('taxable')}</Checkbox>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Tabs.TabPane>
            {tagsEnabled && (
              <Tabs.TabPane key="tags" tab={t('tags')} forceRender>
                <Row>
                  <Col half>
                    {user.coreUserSettings.tagSettings.tag2Enabled && (
                      <Form.Item label={customer.tagSettings.tag2}>
                        {this.props.form.getFieldDecorator('operatorDisplayName', {
                          rules: [
                            ...(customer.tagSettings.tag2Required
                              ? [
                                  {
                                    required: true,
                                    message: t('requiredField'),
                                  },
                                ]
                              : []),
                            ...(customer.tagSettings.tag2Restricted
                              ? [
                                  {
                                    validator: (rule, value, callback) => {
                                      callback(
                                        isEmpty(value) ||
                                          (this.state.operators?.items ?? [])
                                            .map((each) => each.displayName)
                                            .includes(value)
                                          ? []
                                          : [new Error(t('invalidValueEntered'))]
                                      )
                                    },
                                  },
                                ]
                              : []),
                          ],
                          initialValue: item.operatorDisplayName,
                          validateTrigger: false,
                        })(
                          <AutoComplete
                            defaultActiveFirstOption={false}
                            dataSource={(operators?.items ?? []).map((each) => each.displayName)}
                            dropdownRender={createDropdownRender(operators)}
                            onChange={(value) => this.setTagValue('operator', value)}
                            onSearch={this.searchOperators}
                          >
                            <Input
                              onPressEnter={stopEvent}
                              autoComplete="off"
                              suffix={<Suffix />}
                              allowClear
                            />
                          </AutoComplete>
                        )}
                      </Form.Item>
                    )}
                    {user.coreUserSettings.tagSettings.tag1Enabled && (
                      <Form.Item label={customer.tagSettings.tag1}>
                        {this.props.form.getFieldDecorator('jobDisplayName', {
                          rules: [
                            ...(customer.tagSettings.tag1Required
                              ? [
                                  {
                                    required: true,
                                    message: t('requiredField'),
                                  },
                                ]
                              : []),
                            ...(customer.tagSettings.tag1Restricted
                              ? [
                                  {
                                    validator: (rule, value, callback) => {
                                      callback(
                                        isEmpty(value) ||
                                          (jobs.items ?? []).map((each) => each.displayName).includes(value)
                                          ? []
                                          : [new Error(t('invalidValueEntered'))]
                                      )
                                    },
                                  },
                                ]
                              : []),
                          ],
                          initialValue: item.jobDisplayName,
                          validateTrigger: false,
                        })(
                          <AutoComplete
                            defaultActiveFirstOption={false}
                            dataSource={(jobs?.items ?? []).map((each) => each.displayName)}
                            dropdownRender={createDropdownRender(jobs)}
                            onChange={(value) => this.setTagValue('job', value)}
                            onSearch={this.searchJobs}
                          >
                            <Input
                              onPressEnter={stopEvent}
                              autoComplete="off"
                              suffix={<Suffix />}
                              allowClear
                            />
                          </AutoComplete>
                        )}
                      </Form.Item>
                    )}
                    {user.coreUserSettings.tagSettings.tag3Enabled && (
                      <Form.Item label={customer.tagSettings.tag3}>
                        {this.props.form.getFieldDecorator('assetDisplayName', {
                          rules: [
                            ...(customer.tagSettings.tag3Required
                              ? [
                                  {
                                    required: true,
                                    message: t('requiredField'),
                                  },
                                ]
                              : []),
                            ...(customer.tagSettings.tag3Restricted
                              ? [
                                  {
                                    validator: (rule, value, callback) => {
                                      callback(
                                        isEmpty(value) ||
                                          (assets?.items ?? [])
                                            .map((each) => each.displayName)
                                            .includes(value)
                                          ? []
                                          : [new Error(t('invalidValueEntered'))]
                                      )
                                    },
                                  },
                                ]
                              : []),
                          ],
                          initialValue: item.assetDisplayName,
                          validateTrigger: false,
                        })(
                          <AutoComplete
                            defaultActiveFirstOption={false}
                            dataSource={(assets?.items ?? []).map((each) => each.displayName)}
                            dropdownRender={createDropdownRender(assets)}
                            onChange={(value) => this.setTagValue('asset', value)}
                            onSearch={this.searchAssets}
                          >
                            <Input
                              onPressEnter={stopEvent}
                              autoComplete="off"
                              suffix={<Suffix />}
                              allowClear
                            />
                          </AutoComplete>
                        )}
                      </Form.Item>
                    )}
                    {['A', 'B', 'C', 'D', 'E'].map(
                      (type) =>
                        user.coreUserSettings.tagSettings[`tag${type}Enabled`] && (
                          <Form.Item key={type} label={customer.tagSettings[`tag${type}`]}>
                            {this.props.form.getFieldDecorator(`tag${type}`, {
                              rules: [
                                ...(customer.tagSettings[`tag${type}Required`]
                                  ? [
                                      {
                                        required: true,
                                        message: t('requiredField'),
                                      },
                                    ]
                                  : []),
                                ...(customer.tagSettings[`tag${type}Restricted`]
                                  ? [
                                      {
                                        type: 'enum',
                                        enum: tagListItems
                                          .filter((each) => each.tagType === type)
                                          .map((each) => each.description),
                                        message: t('invalidValueEntered'),
                                      },
                                    ]
                                  : []),
                              ],
                              initialValue: get(item, `tag${type}`),
                              validateTrigger: false,
                            })(
                              customer.tagSettings[`tag${type}ShowList`] ? (
                                <AutoComplete
                                  defaultActiveFirstOption={false}
                                  dataSource={tagListItems
                                    .filter((each) => each.tagType === type)
                                    .map((each) => each.description)}
                                  filterOption={filterByValue}
                                  onChange={(value) => this.setItemValue(`tag${type}`, value)}
                                >
                                  <Input
                                    onPressEnter={stopEvent}
                                    autoComplete="off"
                                    suffix={<Suffix />}
                                    allowClear
                                  />
                                </AutoComplete>
                              ) : (
                                <Input onPressEnter={stopEvent} autoComplete="off" allowClear />
                              )
                            )}
                          </Form.Item>
                        )
                    )}
                  </Col>
                </Row>
              </Tabs.TabPane>
            )}
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
          {!readOnly && (
            <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
              {t('save')}
            </Button>
          )}
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
